import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import { fetchAllStudyTracking, formatDuration, detectWeaknesses } from '../../utils/studyTracking';
import { buildQuizAiReport } from '../../utils/studentQuizzes';
import { downloadExcelCsv } from '../../utils/exportExcel';
import { printElementAsPdf } from '../../utils/exportPdf';

export default function AdminStudentProgressPanel() {
  const [students, setStudents] = useState([]);
  const [trackingMap, setTrackingMap] = useState({});
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [usersSnap, tracking] = await Promise.all([
          getDocs(query(collection(db, 'users'), where('role', '==', 'student'), limit(200))),
          fetchAllStudyTracking(100),
        ]);
        const trackById = {};
        tracking.forEach((t) => { trackById[t.userId] = t; });
        setTrackingMap(trackById);

        const list = usersSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
        setStudents(list);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  useEffect(() => {
    if (!selected?.uid) {
      setQuizzes([]);
      return undefined;
    }
    let cancelled = false;
    getDocs(collection(db, 'userQuizzes', selected.uid, 'items'))
      .then((snap) => {
        if (!cancelled) setQuizzes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        if (!cancelled) setQuizzes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selected?.uid]);

  const filtered = students.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return [s.displayName, s.email, s.registrationNumber].join(' ').toLowerCase().includes(q);
  });

  function handleExportExcel() {
    const headers = ['Name', 'Email', 'Reg No', 'Login Days', 'Total Study Time', 'Last Active'];
    const rows = filtered.map((s) => {
      const t = trackingMap[s.uid];
      return [
        s.displayName || '',
        s.email || '',
        s.registrationNumber || '',
        t?.loginDays || 0,
        formatDuration(t?.totalActiveSeconds || 0),
        t?.lastActiveAt?.toDate?.()?.toLocaleDateString?.() || '—',
      ];
    });
    downloadExcelCsv('SBA-student-progress.csv', headers, rows);
  }

  const selTracking = selected ? trackingMap[selected.uid] : null;
  const quizReport = buildQuizAiReport(quizzes);
  const weaknesses = selected
    ? detectWeaknesses({ subjects: [], tracking: selTracking, quizzes })
    : [];

  return (
    <div style={s.wrap}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Student progress monitoring</h2>
          <p style={s.sub}>
            Daily study time, lecture and module quiz monitor, and AI reports for every enrolled student.
          </p>
        </div>
        <div style={s.actions}>
          <button type="button" onClick={handleExportExcel} style={s.exportBtn}>
            Download Excel
          </button>
          <button
            type="button"
            onClick={() => printElementAsPdf('admin-progress-report', 'Student Progress Report')}
            style={s.exportBtn}
          >
            Download PDF
          </button>
        </div>
      </div>

      <input
        type="search"
        placeholder="Search student name, email, reg no…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={s.search}
      />

      {loading ? (
        <p style={s.loading}>Loading student records…</p>
      ) : (
        <div style={s.layout}>
          <div style={s.list}>
            {filtered.map((stu) => {
              const t = trackingMap[stu.uid];
              return (
                <button
                  key={stu.uid}
                  type="button"
                  style={s.row(selected?.uid === stu.uid)}
                  onClick={() => setSelected(stu)}
                >
                  <strong>{stu.displayName || stu.email}</strong>
                  <span style={s.meta}>{stu.registrationNumber || 'No reg no'}</span>
                  <span style={s.meta}>
                    {t ? `${formatDuration(t.totalActiveSeconds || 0)} studied · ${t.loginDays || 0} days` : 'No activity yet'}
                  </span>
                </button>
              );
            })}
          </div>

          <div id="admin-progress-report" style={s.detail}>
            {!selected ? (
              <p style={s.empty}>Select a student to view detailed progress</p>
            ) : (
              <>
                <h3 style={s.detailTitle}>{selected.displayName || selected.email}</h3>
                <p style={s.detailMeta}>Reg No: {selected.registrationNumber || '—'} · {selected.email}</p>

                <div style={s.statsGrid}>
                  <div style={s.stat}>
                    <span style={s.statVal}>{selTracking?.loginDays || 0}</span>
                    <span style={s.statLabel}>Days portal opened</span>
                  </div>
                  <div style={s.stat}>
                    <span style={s.statVal}>{formatDuration(selTracking?.totalActiveSeconds || 0)}</span>
                    <span style={s.statLabel}>Total active study</span>
                  </div>
                  <div style={s.stat}>
                    <span style={s.statVal}>{formatDuration(selTracking?.totalIdleSeconds || 0)}</span>
                    <span style={s.statLabel}>Idle time</span>
                  </div>
                </div>

                <h4 style={s.sectionTitle}>AI quiz monitor</h4>
                <p style={s.empty}>{quizReport.narrative}</p>
                {quizzes.filter((q) => (q.attempts || 0) > 0).length === 0 ? (
                  <p style={s.empty}>No lecture or module quizzes taken yet.</p>
                ) : (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Quiz</th>
                        <th style={s.th}>Type</th>
                        <th style={s.th}>Score</th>
                        <th style={s.th}>Tries</th>
                        <th style={s.th}>Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quizzes
                        .filter((q) => (q.attempts || 0) > 0)
                        .sort((a, b) => Number(b.lastPct || 0) - Number(a.lastPct || 0))
                        .map((q) => (
                          <tr key={q.id}>
                            <td style={s.td}>{q.title || q.id}</td>
                            <td style={s.td}>{q.kind === 'module' ? 'Module' : q.kind === 'extended' ? 'RLA ER' : 'Lecture'}</td>
                            <td style={s.td}>{q.lastPct != null ? `${q.lastPct}%` : '—'}</td>
                            <td style={s.td}>{q.attempts || 0}</td>
                            <td style={s.td}>{q.passed ? 'Passed' : 'Needs retry'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                )}

                <h4 style={s.sectionTitle}>Daily log (recent)</h4>
                {selTracking?.dailyLogs ? (
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Date</th>
                        <th style={s.th}>Active</th>
                        <th style={s.th}>Idle</th>
                        <th style={s.th}>Logins</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(selTracking.dailyLogs)
                        .sort(([a], [b]) => b.localeCompare(a))
                        .slice(0, 14)
                        .map(([day, log]) => (
                          <tr key={day}>
                            <td style={s.td}>{day}</td>
                            <td style={s.td}>{formatDuration(log.activeSeconds)}</td>
                            <td style={s.td}>{formatDuration(log.idleSeconds)}</td>
                            <td style={s.td}>{log.loginCount || 0}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={s.empty}>No daily logs yet</p>
                )}

                <h4 style={s.sectionTitle}>AI weakness detection</h4>
                {weaknesses.length === 0 ? (
                  <p style={s.good}>No major weaknesses detected — keep studying!</p>
                ) : (
                  <ul style={s.weakList}>
                    {weaknesses.map((w, i) => (
                      <li key={i} style={s.weakItem(w.severity)}>
                        <strong>{w.subject}:</strong> {w.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const s = {
  wrap: { flex: 1, minHeight: 0, padding: '24px 28px', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  title: { fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 18, fontWeight: 800, color: '#4b00d1', marginBottom: 6 },
  sub: { fontSize: 13, color: '#64748b', margin: 0 },
  actions: { display: 'flex', gap: 8 },
  exportBtn: {
    padding: '9px 14px',
    borderRadius: 10,
    border: 'none',
    background: '#1b365d',
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    cursor: 'pointer',
  },
  search: {
    width: '100%',
    maxWidth: 400,
    padding: '10px 14px',
    borderRadius: 10,
    border: '1.5px solid #e2e8f0',
    fontSize: 14,
    marginBottom: 16,
  },
  loading: { color: '#94a3b8', padding: 40, textAlign: 'center' },
  layout: { display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, minHeight: 400 },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 520, overflowY: 'auto' },
  row: (active) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '12px 14px',
    borderRadius: 10,
    border: active ? '1.5px solid #4b00d1' : '1px solid #e2e8f0',
    background: active ? '#f5f3ff' : '#fff',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: 13,
  }),
  meta: { fontSize: 11, color: '#94a3b8' },
  detail: {
    background: '#fff',
    borderRadius: 14,
    border: '1px solid #e2e8f0',
    padding: 20,
  },
  detailTitle: { margin: '0 0 4px', fontSize: 17, fontWeight: 800, color: '#1b365d' },
  detailMeta: { fontSize: 12, color: '#64748b', marginBottom: 16 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 },
  stat: { background: '#f8fafc', borderRadius: 12, padding: 14, textAlign: 'center' },
  statVal: { display: 'block', fontSize: 20, fontWeight: 800, color: '#4b00d1' },
  statLabel: { fontSize: 11, color: '#64748b' },
  sectionTitle: { fontSize: 14, fontWeight: 800, color: '#334155', margin: '16px 0 10px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '8px 6px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: 10, textTransform: 'uppercase' },
  td: { padding: '8px 6px', borderBottom: '1px solid #f1f5f9' },
  empty: { color: '#94a3b8', fontSize: 13 },
  good: { color: '#059669', fontSize: 13, fontWeight: 600 },
  weakList: { margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
  weakItem: (severity) => ({
    padding: '10px 12px',
    borderRadius: 10,
    fontSize: 13,
    background: severity === 'high' ? '#fef2f2' : '#fffbeb',
    border: `1px solid ${severity === 'high' ? '#fecaca' : '#fde68a'}`,
  }),
};
